import { api } from '@/lib/axios'
import { downloadFile } from '@/lib/download'
import type { CalcLv, CalcLvPreview, Calculation } from '../types/calculation.types'

/** AP-37 4.11 — LV einlesen (`/calculations/:id/lv`), Baustein `CALCULATION.LV`. */
const basis = (id: string) => `/calculations/${id}/lv`

const formular = (datei: File, felder: Record<string, string | undefined>) => {
  const f = new FormData()
  f.append('file', datei)
  for (const [k, v] of Object.entries(felder)) if (v !== undefined) f.append(k, v)
  return f
}

export type LvZeichensatz = 'cp850' | 'latin1' | 'utf-8'

export const fetchLv = (id: string) => api.get<{ data: CalcLv | null }>(basis(id)).then((r) => r.data.data)

export const inspectLv = (id: string, datei: File, encoding?: LvZeichensatz) =>
  api.post<{ data: CalcLvPreview }>(`${basis(id)}/inspect`, formular(datei, { encoding })).then((r) => r.data.data)

export const importLv = (id: string, datei: File, opts: { encoding?: LvZeichensatz; headFields: boolean }) =>
  api.post<{ data: CalcLv }>(basis(id), formular(datei, { encoding: opts.encoding, headFields: String(opts.headFields) })).then((r) => r.data.data)

export const reviseLv = (id: string, datei: File, encoding?: LvZeichensatz) =>
  api.post<{ data: Calculation }>(`${basis(id)}/revision`, formular(datei, { encoding })).then((r) => r.data.data)

export const setLvUnit = (id: string, dto: { raw: string; unitId: string | null; remember: boolean }) =>
  api.post<{ data: { updated: number } }>(`${basis(id)}/units`, dto).then((r) => r.data.data)

/** Die Originaldatei — nur mit Anmeldung, darum über `downloadFile` statt Link. */
export async function ladeLvDatei(id: string, name: string): Promise<void> {
  const res = await api.get<ArrayBuffer>(`${basis(id)}/file`, { responseType: 'arraybuffer' })
  downloadFile(res.data, name, String(res.headers['content-type'] ?? 'application/octet-stream').split(';')[0])
}

/** AP-37 4.12 — die Prüfliste vor der Abgabe: warnt, sperrt nicht. */
export type LvCheckCode = 'GAP_TOO_LONG' | 'GAP_REQUIRED_OPEN' | 'GAP_OPEN' | 'GAP_OPTIONAL_OPEN' | 'PRICE_MISSING' | 'PRICE_MISSING_OPTIONAL' | 'UNIT_UNTRANSLATED' | 'SURCHARGE_PERCENT_MISSING' | 'PRICE_SPLIT_MISSING' | 'MANUAL_DEVIATION'
export interface LvCheck {
  ok: boolean
  threshold: number
  discountPercent: number | null
  requiresPriceSplit: boolean
  counts: Record<LvCheckCode, number>
  items: { code: LvCheckCode; positionId: string; label: string; shortText: string; detail?: string }[]
}
export const checkLv = (id: string) => api.get<{ data: LvCheck }>(`${basis(id)}/check`).then((r) => r.data.data)

export const fetchLvUnitMemory = () => api.get<{ data: { raw: string; code: string }[] }>('/calculation-settings/lv-units').then((r) => r.data.data)
export const rememberLvUnit = (raw: string, code: string | null) =>
  api.post<{ data: { raw: string; code: string }[] }>('/calculation-settings/lv-units', { raw, code }).then((r) => r.data.data)

// ── Detailblatt K (K-ONLV) — LV ausgeben ────────────────────────────────────

export interface LvExportOptionen {
  vatPercent?: number
  reverseCharge?: boolean
  bidderNumber?: number | null
  offerDate?: string
  notOfferedConfirm?: boolean
  withoutDiscount?: boolean
  markSubmitted?: boolean
  withPrint?: boolean
  withForms?: boolean
  withK3?: boolean
}

export interface LvExportSummen {
  lvTotal: number
  discounts: number
  net: number
  vatPercent: number
  vat: number
  gross: number
  labor: number | null
  groups: { level: string; oz: string; total: number; discount: number; totalAfterDiscount: number }[]
}

export interface LvExportVorschau {
  format: 'ONLV' | 'GAEB'
  formatVersion: string
  supported: boolean
  /** GAEB 3.1/3.2: geschrieben und selbst geprüft, ohne amtliches Schema (K-62). */
  schemaChecked: boolean
  /** K-9: das ONLV ist aus diesem Eingang erzeugt (`B2063`) */
  generatedFrom: string | null
  /** Hinweise des Erzeugers (Einheiten, Zuschlagspositionen, neu nummeriert) */
  notes: string[]
  lvKind: string | null
  isTender: boolean
  vatPercent: number
  discount: { percent: number; level: string | null; perPriceShare: boolean; allowed: boolean } | null
  withoutPrice: { count: number; items: { oz: string; shortText: string }[] }
  notOffered: number
  totals: LvExportSummen
  withPriceShares: boolean
  bidder: { name: string; street: string | null; zip: string | null; city: string | null; country: string | null; phone: string | null; email: string | null; complete: boolean }
  dropped: string[]
  check: { ok: boolean; counts: Record<LvCheckCode, number> }
  canSubmit: boolean
  status: string
}

/** Eine gespeicherte Ausgabe (Detailblatt K 7.2) — Beilagen stehen unter ihrer Abgabe. */
export interface LvAusgabe {
  id: string
  calculationId: string
  revision: number
  submissionId: string
  kind: 'OFFER_FILE' | 'PRINT' | 'FORM_K3' | 'FORM_K7'
  format: string
  formatVersion: string | null
  fileName: string
  sha256: string
  totals: LvExportSummen
  lockedRevision: boolean
  createdAt: string
  createdByName: string | null
  thisRevision: boolean
  attachments?: LvAusgabe[]
}

export const previewLvExport = (id: string, dto: LvExportOptionen) =>
  api.post<{ data: LvExportVorschau }>(`${basis(id)}/export/preview`, dto).then((r) => r.data.data)
export const exportLv = (id: string, dto: LvExportOptionen) => api.post<{ data: LvAusgabe }>(`${basis(id)}/export`, dto).then((r) => r.data.data)
export const fetchAusgaben = (id: string) => api.get<{ data: LvAusgabe[] }>(`/calculations/${id}/exports`).then((r) => r.data.data)
/** Download- und Mail-Anfrage derselben Datei (`export-per-mail.md`). */
export const ausgabeAnfrage = (id: string, eid: string) => ({ url: `/calculations/${id}/exports/${eid}/file`, params: {} })
export async function ladeAusgabe(id: string, a: Pick<LvAusgabe, 'id' | 'fileName'>): Promise<void> {
  const res = await api.get<ArrayBuffer>(`/calculations/${id}/exports/${a.id}/file`, { responseType: 'arraybuffer' })
  downloadFile(res.data, a.fileName, String(res.headers['content-type'] ?? 'application/octet-stream').split(';')[0])
}

/** Detailblatt K 5 — Druckbild: Anfrage für Download und E-Mail (`export-per-mail.md`). */
export interface LvDruckOptionen {
  longText?: boolean
  preliminary?: boolean
  blank?: boolean
  summaryOnly?: boolean
}
export const druckbildAnfrage = (id: string, format: string, orientation: string | undefined, o: LvDruckOptionen) => ({
  url: `${basis(id)}/print`,
  params: {
    format,
    ...(orientation ? { orientation } : {}),
    ...(o.longText ? { longText: '1' } : {}),
    ...(o.preliminary ? { preliminary: '1' } : {}),
    ...(o.blank ? { blank: '1' } : {}),
    ...(o.summaryOnly ? { summaryOnly: '1' } : {}),
  },
})
